import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { buildTaskTree } from '../../../../features/tasks/utils/taskUtils';
import { trackEvent } from '../../../../shared/utils/analytics';
import { clearLoggedOutDraft } from '../../../../shared/hooks/useTasks';
import './SignupLoginModal.css';

const MAX_INVITE_CODE_LENGTH = 128;
/** Sanitize invite code from URL: trim, length cap, alphanumeric + hyphen/underscore only (no XSS/injection). */
function sanitizeInviteCodeFromQuery(raw) {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (trimmed.length > MAX_INVITE_CODE_LENGTH) return trimmed.slice(0, MAX_INVITE_CODE_LENGTH);
  return trimmed.replace(/[^a-zA-Z0-9_-]/g, '');
}

function SignupLoginModal({
  isOpen,
  onClose,
  tasks = [],
  currentFileId = null,
  currentFileName = null,
  loggedOutWorkspaceSnapshot = null,
  saveRequiredMessage = '',
  initialMode = 'signup',
  loginOnly = false,
  source = null,
}) {
  const isSaveVariant = source === 'save';
  const [mode, setMode] = useState(initialMode); // 'login' or 'signup'
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteFromQuery, setInviteFromQuery] = useState(false);
  const [showInviteCodeInput, setShowInviteCodeInput] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginLinkSent, setLoginLinkSent] = useState(false);
  const emailInputRef = useRef(null);
  const { requestMagicLink, signup, joinWaitlist } = useAuth();

  // Ensure mode is 'login' when loginOnly is true; force signup when save variant
  useEffect(() => {
    if (loginOnly && mode !== 'login') {
      setMode('login');
    } else if (isSaveVariant && mode !== 'signup') {
      setMode('signup');
    }
  }, [loginOnly, isSaveVariant, mode]);

  // Reset form when modal opens; prefill invite from ?invite=CODE when present
  useEffect(() => {
    if (isOpen) {
      setMode(loginOnly ? 'login' : isSaveVariant ? 'signup' : initialMode);
      setEmail('');
      setError('');
      setLoginLinkSent(false);
      setWaitlistJoined(false);
      const params = new URLSearchParams(window.location.search);
      const rawInvite = params.get('invite');
      const sanitized = sanitizeInviteCodeFromQuery(rawInvite ?? '');
      if (sanitized) {
        setInviteCode(sanitized);
        setInviteFromQuery(true);
        setShowInviteCodeInput(false);
      } else {
        setInviteCode('');
        setInviteFromQuery(false);
        setShowInviteCodeInput(false);
      }
      const variant = isSaveVariant ? 'save' : 'signup';
      const eventName = `auth_modal_opened_${variant}`;
      trackEvent(eventName, {
        source: source || undefined
      });
    }
  }, [isOpen, loginOnly, initialMode, isSaveVariant, source]);

  // Autofocus email field when save dialog opens (defer so portaled content has mounted)
  useEffect(() => {
    if (!isOpen || !isSaveVariant) return;
    const id = setTimeout(() => {
      emailInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen, isSaveVariant]);

  if (!isOpen) return null;

  const isWaitlistFlow = mode === 'signup' && !inviteFromQuery && !showInviteCodeInput;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const variant = isSaveVariant ? 'save' : 'signup';
    const submitEventName = `auth_modal_submit_started_${variant}`;
    trackEvent(submitEventName, {
      mode,
      source: source || undefined
    });

    try {
      if (mode === 'login') {
        const result = await requestMagicLink(email);
        if (result.success) {
          setLoginLinkSent(true);
        } else {
          setError(result.error || 'Failed to send sign-in link');
        }
        setIsLoading(false);
        return;
      }

      if (isWaitlistFlow) {
        const result = await joinWaitlist(email);
        if (result.success) {
          setWaitlistJoined(true);
        } else {
          setError(result.error || 'Failed to join waitlist');
        }
        setIsLoading(false);
        return;
      }

      const result = await signup(email, inviteCode);

      if (result.success) {
        const workspaceTaskCount = loggedOutWorkspaceSnapshot?.tasksByFileId
          ? Object.values(loggedOutWorkspaceSnapshot.tasksByFileId).reduce(
            (sum, listTasks) => sum + (Array.isArray(listTasks) ? listTasks.length : 0),
            0
          )
          : tasks.length;
        const workspaceFileCount = Array.isArray(loggedOutWorkspaceSnapshot?.files)
          ? loggedOutWorkspaceSnapshot.files.length
          : (currentFileId ? 1 : 0);
        const shouldSaveWorkspace = mode === 'signup' && isSaveVariant && (workspaceFileCount > 0 || workspaceTaskCount > 0);
        if (shouldSaveWorkspace) {
          try {
            await saveWorkspaceAsFirstData();
          } catch (saveErr) {
            console.error('Error saving file/tasks:', saveErr);
            trackEvent('sign_up_failed', {
              variant,
              source: source || undefined,
              step: 'save_workspace'
            });
            setError(saveErr.message || 'Failed to save your tasks. Please try again.');
            setIsLoading(false);
            return;
          }
        } else {
          try {
            await createDefaultFile();
          } catch (createErr) {
            console.error('Error creating default file:', createErr);
            trackEvent('sign_up_failed', {
              variant,
              source: source || undefined,
              step: 'create_file'
            });
            setError(createErr.message || 'Failed to create your first file. Please try again.');
            setIsLoading(false);
            return;
          }
        }
        trackEvent('sign_up', {
          method: 'magic_link',
          variant,
          source: source || undefined,
          invite_used: !!(inviteCode && inviteCode.trim()),
          invite_from_url: inviteFromQuery
        });
        clearLoggedOutDraft();
        onClose();
        window.location.reload();
      } else {
        trackEvent('sign_up_failed', {
          variant,
          source: source || undefined,
          step: 'signup_api'
        });
        setError(result.error || 'An error occurred');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error during login/signup:', err);
      trackEvent('sign_up_failed', {
        variant,
        source: source || undefined,
        step: 'unknown'
      });
      setError(err.message || 'An error occurred');
      setIsLoading(false);
    }
  };

  const createDefaultFile = async () => {
    const fileResponse = await fetch('/api/files.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ name: 'My todolist' })
    });
    if (!fileResponse.ok) {
      const errorText = await fileResponse.text();
      let error;
      try {
        error = JSON.parse(errorText);
      } catch (e) {
        error = { error: errorText || 'Failed to create file' };
      }
      throw new Error(error.error || 'Failed to create file');
    }
  };

  const getWorkspacePayload = () => {
    if (loggedOutWorkspaceSnapshot?.files && loggedOutWorkspaceSnapshot?.tasksByFileId) {
      const files = (loggedOutWorkspaceSnapshot.files || [])
        .filter((f) => f && f.id != null && typeof f.name === 'string')
        .map((f, idx) => ({
          client_id: String(f.id),
          name: (f.name || '').trim() || `List ${idx + 1}`,
          order: Number.isFinite(Number(f.order)) ? Number(f.order) : idx + 1,
          archived: !!f.archived,
        }));
      const tasksPayload = [];
      let nextClientId = 1;
      files.forEach((file) => {
        const fileTasks = Array.isArray(loggedOutWorkspaceSnapshot.tasksByFileId[file.client_id])
          ? loggedOutWorkspaceSnapshot.tasksByFileId[file.client_id]
          : [];
        const taskTree = buildTaskTree(fileTasks);
        const addTasksToBatch = (taskList, parentClientId = null) => {
          for (let i = 0; i < taskList.length; i++) {
            const task = taskList[i];
            const clientId = `t${nextClientId++}`;
            tasksPayload.push({
              client_id: clientId,
              file_client_id: file.client_id,
              parent_client_id: parentClientId,
              text: task.text || '',
              status: task.status || 'incomplete',
              due_date: task.due_date || null,
              is_later_date: task.is_later_date === true,
              order: i + 1,
            });
            if (task.subtasks && task.subtasks.length > 0) {
              addTasksToBatch(task.subtasks, clientId);
            }
          }
        };
        addTasksToBatch(taskTree, null);
      });
      return {
        files,
        tasks: tasksPayload,
        current_file_client_id: loggedOutWorkspaceSnapshot.currentFileId
          ? String(loggedOutWorkspaceSnapshot.currentFileId)
          : null,
      };
    }

    // Backward-compatible fallback: save current list only.
    const fileClientId = String(currentFileId || 'default');
    const fileName = (currentFileName && currentFileName.trim()) || 'My todolist';
    const taskTree = buildTaskTree(tasks);
    const batchTasks = [];
    let nextClientId = 1;
    const addTasksToBatch = (taskList, parentClientId = null, orderOffset = 0) => {
      for (let i = 0; i < taskList.length; i++) {
        const task = taskList[i];
        const order = orderOffset + i + 1;
        const clientId = `t${nextClientId++}`;
        batchTasks.push({
          client_id: clientId,
          file_client_id: fileClientId,
          parent_client_id: parentClientId,
          text: task.text || '',
          status: task.status || 'incomplete',
          due_date: task.due_date || null,
          is_later_date: task.is_later_date === true,
          order,
        });
        if (task.subtasks && task.subtasks.length > 0) {
          addTasksToBatch(task.subtasks, clientId, 0);
        }
      }
    };
    addTasksToBatch(taskTree, null, 0);
    return {
      files: [{ client_id: fileClientId, name: fileName, order: 1, archived: false }],
      tasks: batchTasks,
      current_file_client_id: fileClientId,
    };
  };

  const saveWorkspaceAsFirstData = async () => {
    const workspacePayload = getWorkspacePayload();
    if (!workspacePayload.files || workspacePayload.files.length === 0) {
      throw new Error('No lists to save');
    }
    const response = await fetch('/api/tasks.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        batch_workspace: workspacePayload,
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      let error;
      try {
        error = JSON.parse(errorText);
      } catch (e) {
        error = { error: errorText || 'Failed to save workspace' };
      }
      throw new Error(error.error || 'Failed to save workspace');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const taskCount = isSaveVariant
    ? (
      loggedOutWorkspaceSnapshot?.tasksByFileId
        ? Object.values(loggedOutWorkspaceSnapshot.tasksByFileId).reduce(
          (sum, listTasks) => sum + (Array.isArray(listTasks) ? listTasks.length : 0),
          0
        )
        : tasks.length
    )
    : 0;
  const listCount = isSaveVariant
    ? (
      Array.isArray(loggedOutWorkspaceSnapshot?.files)
        ? loggedOutWorkspaceSnapshot.files.length
        : 1
    )
    : 0;

  const modalContent = (
    <div className="signup-login-modal-backdrop" onClick={handleBackdropClick}>
      <div className="signup-login-modal">
        <button className="signup-login-modal-close text-non-selectable" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4 L4 12 M4 4 L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
        
        {isSaveVariant && (
          <p className="signup-login-modal-save-header">
            Save {taskCount} {taskCount === 1 ? 'task' : 'tasks'} in {listCount} {listCount === 1 ? 'list' : 'lists'}
          </p>
        )}
        {isSaveVariant && saveRequiredMessage && (
          <div className="signup-login-modal-error">
            {saveRequiredMessage}
          </div>
        )}

        {!loginOnly && !isSaveVariant && (
          <div className="signup-login-modal-header">
            <button
              className={`signup-login-modal-tab text-non-selectable ${mode === 'login' ? 'active' : ''}`}
              onClick={() => {
                setMode('login');
                setError('');
                setLoginLinkSent(false);
              }}
            >
              Log In
            </button>
            <button
              className={`signup-login-modal-tab text-non-selectable ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => {
                setMode('signup');
                setError('');
              }}
            >
              Sign Up
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="signup-login-modal-form">
          <div className="signup-login-modal-field">
            <label htmlFor="email">Email</label>
            <input
              ref={emailInputRef}
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder={
                mode === 'login'
                  ? 'Enter your email to get sign-in link'
                  : isWaitlistFlow
                    ? 'Enter your email to join the waitlist'
                    : 'Email only used for sign-in links'
              }
            />
          </div>

          {mode === 'signup' && !inviteFromQuery && showInviteCodeInput && (
            <div className="signup-login-modal-field">
              <label htmlFor="inviteCode">Invite Code</label>
              <input
                id="inviteCode"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                autoComplete="off"
                placeholder="Enter invite code"
              />
            </div>
          )}

          {mode === 'login' && loginLinkSent && (
            <div className="signup-login-modal-recover-message" style={{ color: 'var(--color-text-secondary, #666)' }}>
              Check your email — we sent a sign-in link to <strong>{email}</strong>. The link expires in 6 hours.
            </div>
          )}

          {error && (
            <div className="signup-login-modal-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="signup-login-modal-submit text-non-selectable"
            disabled={isLoading || (isWaitlistFlow && waitlistJoined)}
          >
            {isLoading
              ? 'Processing...'
              : isWaitlistFlow
                ? 'Join waitlist'
                : isSaveVariant
                  ? 'Save & create account'
                  : mode === 'signup'
                    ? 'Sign Up'
                    : 'Send sign-in link'}
          </button>
          {isWaitlistFlow && !waitlistJoined && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="signup-login-modal-invite-link text-non-selectable"
                onClick={() => {
                  setShowInviteCodeInput(true);
                  setError('');
                }}
              >
                Have invite code?
              </button>
            </div>
          )}
          {isWaitlistFlow && waitlistJoined && (
            <div className="signup-login-modal-recover-message" style={{ marginTop: 10 }}>
              Thanks - you&apos;re on the waitlist. We&apos;ll email an invite code when it&apos;s available.
            </div>
          )}
          {(mode === 'signup' || isSaveVariant) && (
            <p className="signup-login-modal-benefits">
              Free · Encrypted · Export anytime<br/>Email only used for sign-in links
            </p>
          )}
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default SignupLoginModal;

